import { createChatInstance } from "@leagueplatform/chat";
import { postStartChat } from "@leagueplatform/live-chat-api";
import { emblemLiveChatClient } from "./emblem-live-chat-client";
import { GenesysWarmHandoffAdapter } from "./genesys-warm-handoff-adapter";

// The bridge implements V2 `ChatClient` directly (no `createChatV2Adapter`
// shim). `config.waitForAgent = false` lives on the bridge itself so the
// composer appears immediately on the direct-human path — otherwise, when
// Genesys' backend resumes a prior disconnected conversation as
// `readOnly:true`, our bridge suppresses CHAT_STARTED (waiting for
// `resetConversation` to force a fresh start), the reset gets rejected by
// the deployment (missing "Read-only conversation disconnect" flag), and
// the session-machine wedges in `starting` — visible as the chat panel
// opening straight into the closed / read-only surface.
// EMBLEM-7436 Waiting Room composer lock — the AC-mandated "no messages
// while queued" is deferred until the readOnly-resume block is unblocked
// (deployment flag flip + Genesys backend grant fix).
const appClient = emblemLiveChatClient;

// Warm-handoff adapter — Eva (`@leagueplatform/ai`) calls this when its
// backend emits `WARM_HANDOFF_PREPARATION_REQUESTED`. Opens the Genesys
// conversation with the AI's context attributes so the human agent picks
// up with continuity.
//
// `getLiveChatConfigs` hits **POST /v1/live-chat** (via `postStartChat`)
// with an empty answers array — same shape iOS' `StartLiveChatRequest`
// sends on the handoff path. That endpoint returns the session-specific
// configs (server-computed `LIVE_CHAT_AVAILABLE` hours gate + routing
// attributes for THIS session), which is what the adapter needs. GET
// `/v1/live-chat-configs` returns the tenant's static config and is not
// interchangeable here — iOS's comment: "Restated rather than reused:
// the SDK's PreChatService is internal to LeaguePreChat and only runs
// from pre-chat, which a handoff skips."
appClient.warmHandoffAdapter = new GenesysWarmHandoffAdapter({
  // Uses the adapter's default `PREPARE_TIMEOUT_MS` (9_500ms). Must
  // stay under the SDK warm-handoff coordinator's hard 10s ceiling
  // (`@leagueplatform/ai:warm-handoff-coordinator.ts` —
  // `ADAPTER_OPERATION_TIMEOUT_MS = 10_000`) so our own timeout wins
  // the race and cleanup runs cleanly. A previous 25_000 override was
  // ineffective and dangerous: the coordinator would abandon the
  // handoff at 10s while our prepare kept running to 25s, orphaning
  // the Genesys session and Eva's UI state.
  getLiveChatConfigs: () =>
    postStartChat([]).then(
      (response) => response.data.attributes.configs ?? undefined
    ),
  // iOS parity (`GenesysWarmHandoffAdapter.swift:48`): refuse to prep
  // while the member is already in a live conversation with an agent.
  hasActiveSession: () => emblemLiveChatClient.hasActiveSession(),
  // Bridge buffers Genesys events during the handoff prepare→activate
  // window so bot auto-responses and disconnects that arrive before the
  // SDK's session-machine has swapped to appClient aren't dropped.
  // Bridge flushes on the post-handoff `USER_START_CHAT`; adapter tells
  // it to discard on cancel / prepare failure.
  onHandoffStarted: () => emblemLiveChatClient.beginHandoffBuffering(),
  onHandoffAbandoned: () => emblemLiveChatClient.discardHandoffBuffer(),
  // Proof-of-delivery await: arm the latch before start, wait for the
  // `.started(newSession, !readOnly)` signal (or reject on
  // `customAttributesSizeExceeded` / readOnly-restore) before returning
  // `accepted`. iOS parity approximation of `Event.ConversationAutostart`.
  beginPreparationTracking: () =>
    emblemLiveChatClient.beginPreparationTracking(),
  // Bandaid for the RC-3 readOnly-recovery path: the adapter fires
  // `startGenesysConversation` directly (bypassing `handleUserStartChat`),
  // so the bridge's `lastStartCustomAttributes` would be undefined when
  // the recovery restart re-runs it. Passing the attributes here lets
  // the bridge replay them with the warm-handoff context intact.
  // Remove once Genesys admin flips the "Read-only conversation
  // disconnect" flag on the current deployment (see
  // project_emblem_7965_genesys_refresh_403.md, RC-3).
  noteStartAttributes: (attributes) =>
    emblemLiveChatClient.noteWarmHandoffAttributes(attributes),
  awaitPreparationDelivered: () =>
    emblemLiveChatClient.awaitPreparationDelivered(),
});

// Shared across the whole app — one conversation, one instance. Held at
// module scope so it survives re-renders of `<Chat>` / `<LiveChatCTA>`.
// `createChatInstance` builds the default `leagueClient` via
// `createAiChatClient({ warmHandoffAdapter: appClient?.warmHandoffAdapter })`
// — that's how Eva gets our Genesys handoff adapter forwarded to it.
export const chatInstance = createChatInstance({
  appClient,
});

/**
 * Opens the shared chat panel for the Eva (`"league"`) client. Encodes
 * the two-event pattern used by every AI entry point (Masonry AI tile,
 * generic `/live-chat` deeplink):
 *   1. A running conversation — Eva or live agent — is resumed, not
 *      ended: iOS parity ("A conversation already running is resumed
 *      instead, from either entry point"), and the live-agent
 *      disclaimer tells members to end that chat themselves before
 *      switching to Eva (EMBLEM-9452). The one exception is a wedged
 *      live-agent session: `inSession` on the Genesys client with no
 *      conversation live, connecting or mid-handoff behind it (partial
 *      handoff / readOnly-resume). Nothing would ever end that one, so
 *      it is force-ended here.
 *   2. If no conversation is running (idle / chatEnded / error), send
 *      `USER_START_CHAT` with `initialClientKind: "league"`.
 *   3. Send `SET_VISIBILITY { isOpen: true }` to pop the panel over
 *      whatever the user lands on next.
 *
 * Human (`"app"`) entry points MUST go through `LiveChatPreChatRoute`
 * — not this function. Direct `USER_START_CHAT` for the app client
 * dispatches with no meta, so the bridge's `handleUserStartChat`
 * would skip the `LIVE_CHAT_AVAILABLE` HOO gate and start Genesys
 * with default routing only. Pre-chat's `POST /v1/live-chat` supplies
 * the authoritative config on the meta that `handleUserStartChat`
 * consumes.
 */
export function openChatSession(): void {
  let state = chatInstance.actor.getSnapshot();
  const isWedgedLiveAgentSession =
    state.matches("inSession") &&
    state.context.activeClient === appClient &&
    !appClient.isConversationLive();
  if (isWedgedLiveAgentSession) {
    chatInstance.actor.send({ type: "USER_END_CHAT" });
    state = chatInstance.actor.getSnapshot();
  }
  const canStart =
    state.matches("idle") ||
    state.matches("chatEnded") ||
    state.matches("error");
  if (canStart) {
    chatInstance.actor.send({
      type: "USER_START_CHAT",
      initialClientKind: "league",
    });
  }
  chatInstance.actor.send({ type: "SET_VISIBILITY", isOpen: true });
}
